import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CMFlex } from "@chartmetric/chartmetric-design-system/components/CMFlex";
import { CMText } from "@chartmetric/chartmetric-design-system/components/CMText";
import { CMButton } from "@chartmetric/chartmetric-design-system/components/CMButton";
import { CMPopover } from "@chartmetric/chartmetric-design-system/components/CMPopover";
import { faChevronDown as fasChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faArrowRightFromBracket as fasArrowRightFromBracket } from "@fortawesome/pro-solid-svg-icons/faArrowRightFromBracket";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useAuth } from "../contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import DarkModeToggle from "./DarkModeToggle";
import { CMBrandIcon } from "@chartmetric/chartmetric-design-system/components/CMBrandIcon";

function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  }
  return email[0].toUpperCase();
}

function UserAvatar({
  name,
  email,
  imageUrl,
}: {
  name: string | null;
  email: string;
  imageUrl: string | null;
}) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        className="rounded-full shrink-0 object-cover"
        style={{ width: 32, height: 32 }}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center rounded-full bg-cm-teal-700 text-white font-semibold shrink-0"
      style={{ width: 32, height: 32, fontSize: "0.75rem" }}
      aria-hidden="true"
    >
      {getInitials(name, email)}
    </div>
  );
}

function SiteHeader() {
  const { t } = useTranslation("common", { keyPrefix: "site" });
  const { user, openAuthModal, signOut } = useAuth();

  return (
    <header className="sticky top-0 z-30">
      <CMFlex
        align="center"
        justify="between"
        background="bg-theme-background"
        px="xl"
        py="md"
        borderColor="lightGray"
        borderSide="bottom"
      >
        <Link to="/" className="flex items-center gap-2 no-underline">
          <CMBrandIcon size="4xl" hideTooltip name="chartmetric" />
          <CMFlex vertical gap="none">
            <CMText value={t("brand")} variant="h3" color="primary" />
            <CMText value={t("tagline")} variant="mini-1" color="tertiary" />
          </CMFlex>
        </Link>

        <CMFlex align="stretch" gap="sm">
          <DarkModeToggle />
          <LanguageSwitcher />
          {user ? (
            <CMPopover.Root>
              <CMPopover.Trigger asChild>
                <button
                  className="flex items-center gap-2 rounded-md px-2 py-1 cursor-pointer hover:bg-gray-100 transition-colors"
                  style={{ background: "none", border: "none" }}
                >
                  <UserAvatar
                    name={user.name}
                    email={user.email}
                    imageUrl={user.imageUrl}
                  />
                  <CMFlex align="start" vertical gap="none">
                    <CMText
                      value={user.name ?? user.email}
                      variant="mini-1"
                      color="primary"
                    />
                    {user.name && (
                      <CMText
                        value={user.email}
                        variant="mini-2"
                        color="tertiary"
                      />
                    )}
                  </CMFlex>
                  <FontAwesomeIcon
                    icon={fasChevronDown}
                    className="text-cm-charcoal-3 ml-1"
                    style={{ fontSize: "0.65rem" }}
                  />
                </button>
              </CMPopover.Trigger>
              <CMPopover.Portal>
                <CMPopover.Content align="end" sideOffset={6} zIndex={40}>
                  <CMButton
                    text={t("signOut")}
                    iconLeft={fasArrowRightFromBracket}
                    variant="secondaryText"
                    size="sm"
                    fullWidth
                    onClick={() => void signOut()}
                  />
                </CMPopover.Content>
              </CMPopover.Portal>
            </CMPopover.Root>
          ) : (
            <CMButton
              text={t("signIn")}
              variant="primary"
              size="sm"
              onClick={openAuthModal}
            />
          )}
        </CMFlex>
      </CMFlex>
    </header>
  );
}

export default SiteHeader;
